import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Alert
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { createHomeBanner, updateHomeBanner, getAllHomeBannersAdmin } from "../../Apis/HomebannersApi";

const BannerForm = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [order, setOrder] = useState(1);
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // Load banner when editing
    useEffect(() => {
        if (isEdit) {
            loadBanner();
        }
    }, [id]);

    const loadBanner = async () => {
        try {
            setLoading(true);
            const data = await getAllHomeBannersAdmin();
            const banners = Array.isArray(data) ? data : data.data || [];
            const banner = banners.find(
                (b) => String(b.id || b._id) === String(id)
            );

            if (banner) {
                setOrder(banner.order || 1);
                if (banner.imageUrl) {
                    setPreviews([banner.imageUrl]);
                }
            } else {
                setErrorMessage("Banner not found.");
            }
        } catch (error) {
            console.error("Error loading banner:", error);
            setErrorMessage("Failed to load banner.");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);

        // Show previews for newly selected files
        const previewUrls = selectedFiles.map(file => URL.createObjectURL(file));
        setPreviews(previewUrls.length > 0 ? previewUrls : previews);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation: require at least one file for create
        if (!isEdit && files.length === 0) {
            setErrorMessage("Please select at least one image.");
            return;
        }

        setLoading(true);
        setSuccessMessage("");
        setErrorMessage("");

        try {
            if (isEdit) {
                // Update banner
                await updateHomeBanner(id, order, files);
                setSuccessMessage("Banner updated successfully!");
            } else {
                await createHomeBanner(order, files);
                setSuccessMessage("Banner created successfully!");
            }

            navigate("/HomeBannersList"); // navigate back to list
        } catch (error) {
            console.error("Error:", error.response ? error.response.data : error.message);
            setErrorMessage("Operation failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={4} sx={{ p: 4, maxWidth: 600, mx: "auto", mt: 6 }}>
            <Typography variant="h5" component="h1" gutterBottom textAlign="center">
                {isEdit ? "Update Home Banner" : "Create Home Banner"}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                    fullWidth
                    label="Order"
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(Number(e.target.value))}
                    margin="normal"
                    required
                    disabled={loading}
                    sx={{ mb: 2 }}
                    InputProps={{
                        sx: {
                            height: 40,
                            padding: "8px 12px",
                            fontSize: "0.875rem"
                        }
                    }}
                />

                <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={loading}
                >
                    {isEdit ? "Choose New Images (Optional)" : "Choose Images"}
                    <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                    />
                </Button>

                {previews.length > 0 && (
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                        {previews.map((src, index) => (
                            <Box
                                key={index}
                                component="img"
                                src={src}
                                alt={`Preview ${index + 1}`}
                                sx={{
                                    width: 100,
                                    height: 100,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid #ccc",
                                    boxShadow: 1
                                }}
                            />
                        ))}
                    </Box>
                )}

                {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>}

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 3 }}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : isEdit ? "Update Banner" : "Upload Banner"}
                </Button>
            </Box>
        </Paper>
    );
};

export default BannerForm;
